"use client";

import { slRoutes } from "@/lib/slRoutes";
import { TeamMember } from "@/lib/teamData";
import {
  Box,
  Link,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { m } from "motion/react";
import Image from "next/image";
import { useState } from "react";

export const SLCardTeamMember = ({
  person,
  isHorizontallyAligned,
  showNameAlways,
}: {
  person: TeamMember;
  isHorizontallyAligned: boolean;
  showNameAlways?: boolean;
}) => {
  const theme = useTheme();
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  const variant = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  };

  const isSm = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });

  return (
    <Link
      href={slRoutes.teamMember(person.slug)}
      style={{
        textDecoration: "none",
        width: "100%",
        height: "100%",
      }}
    >
      <Box
        component={m.div}
        initial={
          showNameAlways || isHorizontallyAligned || isSm ? "visible" : "hidden"
        }
        whileHover={{ y: -8 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: theme.spacing(1),
          flexDirection: isHorizontallyAligned && !isSm ? "row" : "column",
          width: "100%",
          maxWidth: "100%",
        }}
      >
        <Box
          component={m.div}
          key={person.fullName}
          sx={{
            position: "relative",
            minWidth: isHorizontallyAligned && !isSm ? "30%" : "100%",
            height: "auto",
            width: isHorizontallyAligned && !isSm ? "50%" : "100%",
            maxWidth: "200px",
            aspectRatio: "1/1",
            borderRadius: `${theme.shape.borderRadius}px`,
            overflow: "clip",
          }}
        >
          {/* Loading skeleton */}
          {imageLoading && (
            <Skeleton
              variant="circular"
              width="100%"
              height="100%"
              animation="wave"
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1,
                borderRadius: `${theme.shape.borderRadius}px`,
              }}
            />
          )}

          {/* Error fallback */}
          {imageError && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: theme.palette.grey[200],
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: theme.palette.text.secondary,
                fontSize: "0.75rem",
                borderRadius: `${theme.shape.borderRadius}px`,
              }}
            >
              Foto nicht verfügbar
            </Box>
          )}

          {/* Optimized image */}
          {!imageError && (
            <Image
              src={person.image[600]}
              alt={person.imageAlt}
              fill
              quality={65}
              loading="lazy"
              style={{
                objectFit: "cover",
                objectPosition: "center",
                transition: "opacity 0.3s ease-in-out",
                opacity: imageLoading ? 0 : 1,
                borderRadius: `${theme.shape.borderRadius}px`,
              }}
              onLoad={() => setImageLoading(false)}
              onError={() => {
                setImageLoading(false);
                setImageError(true);
              }}
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 200px"
            />
          )}
        </Box>
        <Box
          component={m.div}
          variants={variant}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing(1.8),
            alignItems: "center",
            justifyContent: "center",
            maxWidth: isHorizontallyAligned && !isSm ? "50%" : "100%",
            width: isHorizontallyAligned && !isSm ? "50%" : "100%",
          }}
        >
          <Typography
            variant="body1"
            textAlign="center"
            sx={{
              mt: theme.spacing(2),
            }}
          >
            {person.fullName}
            <br />
            {person.role}
          </Typography>
          {/* <Typography
          variant="body2"
          color="text.secondary"
          fontWeight={600}
          textAlign="center"
          width="100%"
        >
          {person.role}
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          <SLTeamMemberExpertiseChips expertise={person.expertise} />
        </Typography> */}
          {/* <Link href={slRoutes.teamMember(person.slug)}>
            <Button
              size="small"
              variant="contained"
              sx={{
                marginTop: theme.spacing(0.5),
                padding: important("3px 12px"),
              }}
            >
              Mehr erfahren
            </Button>
          </Link> */}
        </Box>
      </Box>
    </Link>
  );
};
